import type { ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Button, toastManager } from '@systemeio/components'
import { MessageCircle01Icon } from '@systemeio/icons'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { LikeButton } from 'shared/components/like/like-button'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import { AudioPlayerAttachment } from 'modules/attachments/components/audio-player'
import { CommentsList } from 'modules/comments/components'
import { CommentsTypeEnum } from 'modules/comments/types'
import { useCommentsCount } from 'modules/community/api/get-comments-count'
import { TextWithMentions } from 'modules/community/components/editor/utils/deserialize-mention-to-html'
import { PostAuthorInfo } from 'modules/community/components/post-announce/author-info'
import { PostActionsMenu } from 'modules/community/components/post/components/post-actions-menu'
import { PostAttachmentsViewer } from 'modules/community/components/post/components/post-attachments-viewer'
import {
  UnderModerationPlaceholder,
  shouldHideUnderModerationContent,
} from 'modules/community/components/under-moderation-placeholder'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'
import type { PostInterface } from 'modules/community/types/post-interface'
import type { CommentsHighlightContext } from '../types'

export const PostView = ({
  post,
  handleApprovePost,
  handleDeclinePost,
  handleDeletePost,
  handleDeclinePostAndBanUser,
  deletePostAndBanUser,
  handleLikeClick,
  isLoading,
  commentsHighlightContext,
  targetCommentId,
}: {
  post: PostInterface
  handleApprovePost?: () => void
  handleDeclinePost?: () => void
  handleDeletePost?: () => Promise<void>
  handleLikeClick?: () => void
  handleDeclinePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  deletePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  isLoading: boolean
  commentsHighlightContext?: CommentsHighlightContext
  targetCommentId?: number
}): ReactNode => {
  const { t } = useTranslation()
  const { user, loading: isUserLoading } = useUser()
  const { isUserMember } = useIsUserMember()
  const router = useRouter()
  const { communityPath } = router.query

  const canManagePostStatus =
    post.status !== PostStatusEnum.Approved &&
    user &&
    (isWsAdmin(user) || isDomainOwner(user) || isWsAssistant(user))

  const hideContent = shouldHideUnderModerationContent(post.underModeration, user)

  const shouldDisplayCommentsCounter = post.status === PostStatusEnum.Approved && !!handleLikeClick

  const { data: commentsCountData } = useCommentsCount({
    communityPath:
      shouldDisplayCommentsCounter && typeof communityPath === 'string' ? communityPath : undefined,
    postsIds: shouldDisplayCommentsCounter ? [post.id] : undefined,
  })

  const commentsCount = commentsCountData?.[0]?.commentsCount ?? post.commentsCount ?? 0

  if (post.status !== PostStatusEnum.Pending && !post.publishedAt) {
    Sentry.captureMessage('Post view has no publishedAt', {
      level: 'error',
      extra: { post },
    })
    return null
  }

  return (
    <>
      <div
        className={twMerge(
          `mx-4 pt-6 font-avertaPE sm:mx-6 md:mx-8`,
          post.status !== PostStatusEnum.Approved && 'pb-6',
        )}
      >
        <div className="mb-5 flex items-center gap-2">
          <PostAuthorInfo post={post} />

          {canManagePostStatus && handleApprovePost && handleDeclinePost && (
            <div className="ml-auto flex gap-3 max-sm:hidden">
              <Button
                variant="primary"
                onClick={handleApprovePost}
                isLoading={isUserLoading || isLoading}
              >
                {t('community.moderation.posts.approve_button.title')}
              </Button>
              <Button
                variant="secondary"
                className="disabled:border-text-secondary disabled:text-text-secondary"
                onClick={handleDeclinePost}
                disabled={post.status === PostStatusEnum.Declined}
                isLoading={isUserLoading || isLoading}
              >
                {t('community.moderation.posts.decline_button.title')}
              </Button>
            </div>
          )}
          <PostActionsMenu
            deletePostAndBanUser={
              deletePostAndBanUser
                ? removeRecentActivitiesChecked =>
                    deletePostAndBanUser(removeRecentActivitiesChecked)
                : undefined
            }
            post={post}
            deletePost={handleDeletePost}
            declinePostAndBanUser={handleDeclinePostAndBanUser}
          />
        </div>
        {canManagePostStatus && handleApprovePost && handleDeclinePost && (
          <div className="mb-2 ml-auto flex gap-3 sm:hidden">
            <Button
              variant="primary"
              onClick={handleApprovePost}
              isLoading={isUserLoading || isLoading}
            >
              {t('community.moderation.posts.approve_button.title')}
            </Button>
            <Button
              variant="secondary"
              className="disabled:border-text-secondary disabled:text-text-secondary"
              onClick={handleDeclinePost}
              disabled={post.status === PostStatusEnum.Declined}
              isLoading={isUserLoading || isLoading}
            >
              {t('community.moderation.posts.decline_button.title')}
            </Button>
          </div>
        )}
        {hideContent ? (
          <UnderModerationPlaceholder type="post" className="pb-1 sm:pb-4" />
        ) : (
          <>
            <h2
              className="pb-1 community-post-title wrap-break-word sm:pb-4"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(post.title),
              }}
            />
            <TextWithMentions
              className="wrap-break-word text-text-primary/90"
              text={post.content}
              mentions={post.mentions}
            />
            {!!post?.attachments?.length &&
              post.attachments.map(
                attachment =>
                  attachment.type === AttachmentTypeEnum.Audio && (
                    <AudioPlayerAttachment
                      attachment={attachment}
                      height="50px"
                      width="100%"
                      className="mt-3"
                      key={attachment.dataFileId}
                    />
                  ),
              )}

            <PostAttachmentsViewer attachments={post.attachments || []} />
          </>
        )}

        {shouldDisplayCommentsCounter && (
          <div className="mt-5.5 flex items-center gap-1">
            <LikeButton
              isLiked={post.isLiked}
              likesCount={post.likesCount}
              handleLikeClick={handleLikeClick}
              postId={post.id}
              readOnly={hideContent}
            />
            <div
              data-testid="post-view-comments-counter"
              className="group flex items-center gap-2 rounded-md px-1.5 py-2 transition-colors hover:bg-bg-accent/20"
            >
              <MessageCircle01Icon className="size-5 text-text-secondary transition-colors group-hover:text-text-link" />
              <span data-testid="post-view-comments-counter-value">{commentsCount || ''}</span>
            </div>
          </div>
        )}
      </div>
      {post.status === PostStatusEnum.Approved && (
        <div id="Comments" className="px-4 font-avertaPE sm:px-6 md:px-8">
          <div className="mt-4 mb-3 text-xl font-semibold text-text-primary md:mt-6 md:mb-5">
            {t('post_details.comments')}
          </div>
          {user && (
            <CommentsList
              commentsType={CommentsTypeEnum.Community}
              onError={() =>
                toastManager.add({
                  type: 'error',
                  title: t('comments.error.error-loading-comments'),
                })
              }
              readOnly={!isUserMember}
              community={{
                postId: post.id,
                communityPath: String(communityPath),
              }}
              commentsHighlightContext={commentsHighlightContext}
              targetCommentId={targetCommentId}
            />
          )}
        </div>
      )}
    </>
  )
}
